import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Input, 
  Button, 
  Typography,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, StarIcon, ClockIcon, ShieldCheckIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Logo from '../utils/logo';
import { colors } from '../utils/colors';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet';
import '../iubenda.css';
import { trackEvent } from "../utils/analytics";

export default function Login() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const getCookie = (name) => {
    return Cookies.get(name);
  };

  const hasCheckedToken = useRef(false);

  useEffect(() => {
    const checkAuthToken = async () => {
      const authToken = getCookie('authToken');

      if (authToken && !hasCheckedToken.current) {
        hasCheckedToken.current = true;
        try {
          const response = await fetch('/api/check_token');
          const data = await response.json();

          if (data.success) {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          setErrorMessage('Votre session a expiré. Veuillez vous reconnecter.');
          setIsErrorModalOpen(true);
        }
      }
    };

    checkAuthToken();
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    
    if (errorParam === 'account_not_found') {
      trackEvent('login_error_parameter', { error: errorParam });
      
      setErrorMessage('Aucun compte n\'est associé à cet e-mail. Veuillez vérifier l\'adresse e-mail saisie. Pour le moment, l\'accès à l\'espace client est réservé aux particuliers.');
      setIsErrorModalOpen(true);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    trackEvent('login_submit', {});

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        trackEvent('login_success', {});
        
        setIsSuccessModalOpen(true);
      } else {
        trackEvent('login_error', {});
        
        setErrorMessage(data.error || 'Une erreur est survenue. Veuillez réessayer.');
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      trackEvent('login_error', {});
      
      console.error('Erreur lors de la demande du lien magique:', error);
      setErrorMessage('Erreur de connexion. Veuillez réessayer.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    setIsLoading(true);
    
    trackEvent('login_resend', {});
    
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.redirect) {
        window.location.href = data.redirect;
      }

      if (response.ok) {
        setIsSuccessModalOpen(true);
      } else {
        trackEvent('login_error', {});
        
        setErrorMessage(data.error || 'Une erreur est survenue lors du renvoi du lien.');
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      trackEvent('login_error', {});
      
      console.error('Erreur lors du renvoi du lien de connexion:', error);
      setErrorMessage('Erreur de connexion. Veuillez réessayer.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: colors.dimoPrimary }}>
      <Helmet>
        <title>Connexion - Espace client Dimo Diagnostic</title>
        <meta name="description" content="Connectez-vous à votre espace personnel Dimo pour gérer vos rendez-vous et demandes." />
      </Helmet>
      
      <Button className="iubenda-tp-btn-login iubenda-cs-preferences-link" id="iubenda-tp-btn">
        <span className="sr-only">Cookie</span>
      </Button>

      {/* Section de connexion - gauche */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo et titre */}
          <div className="mb-8 text-center lg:text-left">
            <div className="mb-6 hidden lg:block">
              <Logo centered={false} className="lg:mx-0" />
            </div>
            <div className="mb-6 lg:hidden">
              <Logo centered={true} className="mx-auto" />
            </div>
            <Typography variant="h2" className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: colors.dimoPrimary }}>
              Bienvenue !
            </Typography>
            <Typography variant="paragraph" color="gray" className="text-sm lg:text-base">
              Connectez-vous à votre espace client
            </Typography>
          </div>

          {/* Formulaire de connexion */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="text-center lg:text-left">
              <Typography variant="small" className="mb-2 font-medium" style={{ color: colors.dimoPrimary }}>
                Adresse e-mail
              </Typography>
              <Input
                type="email"
                placeholder="Saisissez votre adresse e-mail"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full !border-gray-300 focus:!border-2"
                labelProps={{
                  className: "hidden",
                }}
                style={{
                  focusBorderColor: colors.dimoPrimary
                }}
              />
            </div>

            <Button
              type="submit"
              className="w-full py-3 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: colors.dimoPrimary }}
              disabled={isLoading}
            >
              {isLoading ? 'Connexion en cours...' : 'Se connecter'}
            </Button>
          </form>

          {/* Lien devenir client */}
          <div className="mt-6 text-center lg:text-left">
            <Typography variant="small" color="gray" className="text-sm mb-2">
              Pas encore de compte ?
            </Typography>
            <a 
              href="https://www.dimo-diagnostic.net/devis-diagnostic-immobilier-tarifs" 
              className="text-sm font-medium hover:underline transition-colors duration-200"
              style={{ color: colors.dimoPrimary }}
            >
              Devenir client
            </a>
          </div>

        </div>
      </div>

      {/* Section marketing - droite (cachée sur mobile) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white overflow-y-auto">
        <div className="max-w-lg mx-auto">
          {/* Titre principal */}
          <div className="text-center mb-8">
            <Typography variant="h3" className="text-3xl lg:text-4xl font-bold mb-4 leading-tight">
              Votre diagnostic immobilier de confiance
            </Typography>
            <Typography variant="paragraph" className="text-lg text-blue-gray-100">
              Expertise reconnue, rapports détaillés et service client dédié pour tous vos besoins immobiliers.
            </Typography>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-3 gap-4 mb-8 text-center">
            <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
              <Typography variant="h4" className="text-2xl font-bold mb-1" style={{ color: colors.dimoPop }}>
                400K+
              </Typography>
              <Typography variant="small" className="text-blue-gray-200 text-xs">
                Diagnostics réalisés
              </Typography>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
              <Typography variant="h4" className="text-2xl font-bold mb-1" style={{ color: colors.dimoPop }}>
                48h
              </Typography>
              <Typography variant="small" className="text-blue-gray-200 text-xs">
                Délai moyen observé
              </Typography>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
              <Typography variant="h4" className="text-2xl font-bold mb-1" style={{ color: colors.dimoPop }}>
                6K+
              </Typography>
              <Typography variant="small" className="text-blue-gray-200 text-xs">
                Avis client Trustpilot
              </Typography>
            </div>
          </div>

          {/* Points forts avec icônes */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: colors.dimoPop }}>
                <ClockIcon className="w-5 h-5" style={{ color: colors.dimoPrimary }} />
              </div>
              <div>
                <Typography variant="paragraph" className="font-semibold mb-1">
                  Rendez-vous rapides
                </Typography>
                <Typography variant="small" className="text-blue-gray-200">
                  Intervention en moyenne sous 48h. Planification en ligne simple et efficace.
                </Typography>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: colors.dimoPop }}>
                <ShieldCheckIcon className="w-5 h-5" style={{ color: colors.dimoPrimary }} />
              </div>
              <div>
                <Typography variant="paragraph" className="font-semibold mb-1">
                  Diagnostics certifiés
                </Typography>
                <Typography variant="small" className="text-blue-gray-200">
                  Conformité garantie et assurance responsabilité civile. Diagnostiqueurs certifiés.
                </Typography>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: colors.dimoPop }}>
                <StarIcon className="w-5 h-5" style={{ color: colors.dimoPrimary }} />
              </div>
              <div>
                <Typography variant="paragraph" className="font-semibold mb-1">
                  Excellence reconnue
                </Typography>
                <Typography variant="small" className="text-blue-gray-200">
                  Note moyenne de 4.6/5 basée sur plus de 6000 avis clients vérifiés sur Trustpilot.
                </Typography>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: colors.dimoPop }}>
                <UserGroupIcon className="w-5 h-5" style={{ color: colors.dimoPrimary }} />
              </div>
              <div>
                <Typography variant="paragraph" className="font-semibold mb-1">
                  100% Salariés
                </Typography>
                <Typography variant="small" className="text-blue-gray-200">
                  Tous nos diagnostiqueurs sont salariés de notre entreprise !
                </Typography>
              </div>
            </div>

          </div>


        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={isSuccessModalOpen} handler={() => setIsSuccessModalOpen(false)} size="xs">
        <DialogHeader className="flex items-center justify-between">
          <Typography variant="h5" color="blue-gray">
            Lien de connexion envoyé
          </Typography>
          <XMarkIcon 
            className="h-5 w-5 cursor-pointer" 
            onClick={() => setIsSuccessModalOpen(false)}
          />
        </DialogHeader>
        <DialogBody divider className="grid place-items-center gap-4">
          <CheckCircleIcon className="h-16 w-16" style={{ color: colors.dimoPrimary }} />
          <Typography color="blue-gray" variant="h4" className="text-center">
            Vérifiez votre boîte mail
          </Typography>
          <Typography className="text-center font-normal">
            Un lien de connexion a été envoyé à votre adresse e-mail. Veuillez consulter votre boîte de réception pour vous connecter.
          </Typography>
        </DialogBody>
        <DialogFooter className="space-x-2">
          <Button 
            variant="outlined" 
            color="blue-gray active:blue-gray"
            onClick={() => setIsSuccessModalOpen(false)}
          >
            Fermer
          </Button>
          <Button 
            style={{ backgroundColor: colors.dimoPrimary }}
            onClick={handleResendLink}
          >
            Renvoyer le lien
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={isErrorModalOpen} handler={() => setIsErrorModalOpen(false)} size="xs">
        <DialogHeader className="flex items-center justify-between">
          <Typography variant="h5" color="blue-gray">
            Erreur de connexion
          </Typography>
          <XMarkIcon 
            className="h-5 w-5 cursor-pointer" 
            onClick={() => setIsErrorModalOpen(false)}
          />
        </DialogHeader>
        <DialogBody divider className="grid place-items-center gap-4">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500" />
          <Typography color="blue-gray" variant="h4" className="text-center">
            Oups ! Une erreur est survenue
          </Typography>
          <Typography className="text-center font-normal">
            {errorMessage}
          </Typography>
        </DialogBody>
        <DialogFooter className="space-x-2">
          <Button 
            style={{ backgroundColor: colors.dimoPrimary }}
            onClick={() => {
              setIsErrorModalOpen(false);
              setEmail('');
            }}
            fullWidth
          >
            Réessayer
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}